import type { AlertIconMode, AlertIconShape, AlertTheme, LeaderboardPeriod, TimerLayout, WidgetConfigState } from "./types";
import { DonateWidget } from "services/main/donate.service";
import { getAlertLayoutPresetDefinition, normalizeAlertLayoutPreset } from "./constants";
import { findWidgetForTab, getAlertDuration, toBooleanValue, toNumberValue, toStringValue } from "./widgetUtils";
import { getWidgetSettings, getWidgetStyles } from "./flowWidgetKindUtils";
export const getWidgetTemplateText = (
  widget: DonateWidget | undefined,
  fallback: string,
) => {
  const gridItemWithText = widget?.gridLayout?.items?.find(
    (item) =>
      typeof item?.data?.template === "string" ||
      typeof item?.data?.text === "string" ||
      typeof item?.settings?.messageTemplate === "string",
  );

  if (gridItemWithText) {
    return (
      gridItemWithText.data?.template ||
      gridItemWithText.data?.text ||
      gridItemWithText.settings?.messageTemplate ||
      fallback
    );
  }

  const nodeWithText = widget?.reactFlow?.nodes?.find(
    (node) => typeof node?.data?.text === "string",
  );

  return nodeWithText?.data?.text || fallback;
};

export const mergeWidgetConfigFromApi = (
  current: WidgetConfigState,
  widgets: DonateWidget[],
): WidgetConfigState => {
  const alertWidget = findWidgetForTab(widgets, "alert");
  const goalWidget = findWidgetForTab(widgets, "goal");
  const timerWidget = findWidgetForTab(widgets, "timer");
  const leaderboardWidget = findWidgetForTab(widgets, "leaderboard");
  const chatWidget = findWidgetForTab(widgets, "chat");

  const alertSettings = getWidgetSettings(alertWidget, "alert");
  const alertStyles = getWidgetStyles(alertWidget, "alert");
  const goalSettings = getWidgetSettings(goalWidget, "goal");
  const goalStyles = getWidgetStyles(goalWidget, "goal");
  const timerSettings = getWidgetSettings(timerWidget, "timer");
  const leaderboardSettings = getWidgetSettings(
    leaderboardWidget,
    "leaderboard",
  );
  const chatSettings = getWidgetSettings(chatWidget, "chat");

  return {
    alert: alertWidget
      ? {
          ...current.alert,
          theme: (alertSettings.theme || current.alert.theme) as AlertTheme,
          layoutPreset: normalizeAlertLayoutPreset(
            alertSettings.layoutPreset || alertSettings.layout,
            alertSettings.theme || current.alert.theme,
          ),
          minAmount: toStringValue(
            alertSettings.minAmount,
            current.alert.minAmount,
          ),
          duration: getAlertDuration(alertSettings, current.alert.duration),
          messageTemplate:
            alertSettings.messageTemplate ||
            getWidgetTemplateText(alertWidget, current.alert.messageTemplate),
          imageUrl: alertSettings.imageUrl || current.alert.imageUrl,
          iconMode: (alertSettings.iconMode ||
            current.alert.iconMode) as AlertIconMode,
          iconUrl:
            alertSettings.iconUrl ||
            alertSettings.imageUrl ||
            current.alert.iconUrl,
          iconEmoji: alertSettings.iconEmoji || current.alert.iconEmoji,
          iconShape: (alertSettings.iconShape ||
            current.alert.iconShape) as AlertIconShape,
          iconSize: toNumberValue(
            alertSettings.iconSize,
            current.alert.iconSize,
          ),
          soundName: alertSettings.soundName || current.alert.soundName,
          volume: toNumberValue(alertSettings.volume, current.alert.volume),
          animation: alertSettings.animation || current.alert.animation,
          effect: "none",
          backgroundStyle: "none",
          accentColor:
            alertStyles.accentColor ||
            alertSettings.accentColor ||
            current.alert.accentColor,
          textColor:
            alertStyles.textColor ||
            alertSettings.textColor ||
            current.alert.textColor,
          showDonorName: toBooleanValue(
            alertSettings.showDonorName,
            current.alert.showDonorName,
          ),
          showAmount: toBooleanValue(
            alertSettings.showAmount,
            current.alert.showAmount,
          ),
          showMessage: toBooleanValue(
            alertSettings.showMessage,
            current.alert.showMessage,
          ),
          showIcon: toBooleanValue(
            alertSettings.showIcon,
            current.alert.showIcon,
          ),
          showBorderGlow: toBooleanValue(
            alertSettings.showBorderGlow,
            current.alert.showBorderGlow,
          ),
        }
      : current.alert,
    goal: goalWidget
      ? {
          ...current.goal,
          title: goalSettings.title || current.goal.title,
          current: toStringValue(goalSettings.current, current.goal.current),
          target: toStringValue(goalSettings.target, current.goal.target),
          endsAt: goalSettings.endsAt || current.goal.endsAt,
          layout: (goalSettings.layout || current.goal.layout) as TimerLayout,
          accentColor:
            goalStyles.accentColor ||
            goalSettings.accentColor ||
            current.goal.accentColor,
          showPercent: toBooleanValue(
            goalSettings.showPercent,
            current.goal.showPercent,
          ),
        }
      : current.goal,
    timer: timerWidget
      ? {
          ...current.timer,
          status: timerSettings.status || current.timer.status,
          label: timerSettings.label || current.timer.label,
          durationMinutes: toStringValue(
            timerSettings.durationMinutes,
            current.timer.durationMinutes,
          ),
          breakMinutes: toStringValue(
            timerSettings.breakMinutes,
            current.timer.breakMinutes,
          ),
          layout: (timerSettings.layout || current.timer.layout) as TimerLayout,
          opacity: toNumberValue(timerSettings.opacity, current.timer.opacity),
          scale: toNumberValue(timerSettings.scale, current.timer.scale),
          showProgress: toBooleanValue(
            timerSettings.showProgress,
            current.timer.showProgress,
          ),
          allowMinus: toBooleanValue(
            timerSettings.allowMinus,
            current.timer.allowMinus,
          ),
        }
      : current.timer,
    leaderboard: leaderboardWidget
      ? {
          ...current.leaderboard,
          title: leaderboardSettings.title || current.leaderboard.title,
          period: (leaderboardSettings.period ||
            current.leaderboard.period) as LeaderboardPeriod,
          limit: toStringValue(
            leaderboardSettings.limit,
            current.leaderboard.limit,
          ),
          showAmounts: toBooleanValue(
            leaderboardSettings.showAmounts,
            current.leaderboard.showAmounts,
          ),
        }
      : current.leaderboard,
    chat: chatWidget
      ? {
          ...current.chat,
          title: chatSettings.title || current.chat.title,
          width: toStringValue(chatSettings.width, current.chat.width),
          height: toStringValue(chatSettings.height, current.chat.height),
          fontSize: toStringValue(chatSettings.fontSize, current.chat.fontSize),
          theme: chatSettings.theme || current.chat.theme,
          showAvatar: toBooleanValue(
            chatSettings.showAvatar,
            current.chat.showAvatar,
          ),
          showBadges: toBooleanValue(
            chatSettings.showBadges,
            current.chat.showBadges,
          ),
        }
      : current.chat,
  };
};
